// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createAppraiserPrice, deleteAppraiserPrice, updateAppraiserPrice } from '../../feature';

// Components
import { ConfirmationModal } from '@/components';
import { Button, Spinner } from '@/ui';
import { AppraiserForm, StyledContainer, StyledTitle } from '../../components';

// Modules
import { StyledSettingsCard } from '@/modules/SettingsCommon';

// Helpers
import { getEntityId } from '@/utils';
import { useGetAppraiserPrice } from '../../hooks';
import { useModal } from '@/hooks';
import { useState } from 'react';
import { IAppraiserPrice } from '@/models/prices.model';
import { TAppraiserFormValues } from '@/modules/Prices/helpers/types';
import {
  StyledDrawer,
  StyledWrapper,
} from '@/modules/Prices/containers/PhotoShootingPricesContainer/PhotoShootingPricesContainerStyles';
import AppraiserCard from '@/modules/Prices/components/AppraiserCard/AppraiserCard';

const AppraiserPricesContainer = () => {
  const { t } = useTranslation('prices');
  const { isLoading, prices } = useGetAppraiserPrice();
  const dispatch = useAppDispatch();

  const { isOpened, closeModal, openModal } = useModal();

  const [deletedId, setDeletedId] = useState<string | null>(null);
  const [editablePrice, setEditablePrice] = useState<IAppraiserPrice | null>(null);

  const handleClose = () => {
    setEditablePrice(null);
    closeModal();
    setDeletedId(null);
  };

  const handleUpdate = async (data: TAppraiserFormValues) => {
    try {
      if (editablePrice) {
        await dispatch(
          updateAppraiserPrice({
            ...data,
            id: getEntityId(editablePrice.PK),
          }),
        ).unwrap();
      } else {
        await dispatch(createAppraiserPrice(data)).unwrap();
      }
      handleClose();
    } catch (error) {}
  };

  const handleConfirmDelete = async () => {
    if (!deletedId) return;

    try {
      await dispatch(deleteAppraiserPrice(getEntityId(deletedId))).unwrap();
      handleClose();
    } catch (error) {}
  };

  return (
    <StyledContainer>
      <StyledWrapper>
        <StyledTitle>{t('title_appraiser')}</StyledTitle>
        <Button type="primary" shape="round" onClick={openModal}>
          {t('button_create')}
        </Button>
      </StyledWrapper>
      <Spinner spinning={isLoading}>
        <StyledSettingsCard>
          {prices?.map((price) => (
            <AppraiserCard key={price.PK} item={price} onDelete={setDeletedId} onEdit={setEditablePrice} />
          ))}
        </StyledSettingsCard>
      </Spinner>
      <StyledDrawer
        footer={
          <Button shape="round" block type="primary" htmlType="submit" form="create-form" loading={isLoading}>
            {editablePrice ? t('button_update') : t('button_create')}
          </Button>
        }
        width="35rem"
        onClose={handleClose}
        open={isOpened || !!editablePrice}
        title={editablePrice ? t('button_update') : t('button_create')}
        destroyOnClose
      >
        <AppraiserForm
          formName="create-form"
          onSubmit={handleUpdate}
          initialValues={{
            description_en: editablePrice?.description_en,
            description_no: editablePrice?.description_no,
            from_area: editablePrice?.from_area,
            name_en: editablePrice?.name_en,
            name_no: editablePrice?.name_no,
            price: editablePrice?.price,
            property_type: editablePrice?.property_type,
            to_area: editablePrice?.to_area ?? undefined,
          }}
        />
      </StyledDrawer>
      <ConfirmationModal
        isOpened={!!deletedId}
        onCancel={handleClose}
        onConfirm={handleConfirmDelete}
        title={t('title_delete_price')}
      />
    </StyledContainer>
  );
};

export default AppraiserPricesContainer;
